import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { DocRecord } from '@shared/types';
import DOMPurify from 'dompurify';
import { EDITOR_PURIFY_CONFIG } from '@/lib/constants';
import { cn } from '@/lib/utils';

export function ViewDocPage() {
  const { slug } = useParams();
  const [doc, setDoc] = useState<DocRecord | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDoc() {
      try {
        const res = await fetch(`/api/docs/${slug}/raw`);
        const result = await res.json() as any;
        if(result.success) {
          setDoc(result.data);
          document.title = `${result.data.title} | DocForge`;
        } else {
          setError(true);
        }
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchDoc();
  }, [slug]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center space-y-4 transition-colors">
      <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
      <p className="font-h3 text-h3 text-on-surface-variant italic">Retrieving from archives...</p>
    </div>
  );

  if (error || !doc) return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6 text-center space-y-6 transition-colors">
      <h1 className="font-h1 text-h1 text-on-surface text-[64px]">Lost Archive</h1>
      <Link to="/" className="flex items-center gap-2 text-primary font-label-caps text-label-caps uppercase tracking-widest hover:underline transition-all">
        <span className="material-symbols-outlined">arrow_back</span>
        <span>Return to the Forge</span>
      </Link>
    </div>
  );

  const sanitized = DOMPurify.sanitize(doc.html || '', EDITOR_PURIFY_CONFIG);

  return (
    <div className="min-h-screen bg-surface-dim md:p-12 p-0 print:p-0 print:bg-white transition-colors duration-500 overflow-y-auto">
      <style>{`
        @media print {
          body { background: white !important; color: black !important; }
          .print-hidden { display: none !important; }
          .doc-content { padding: 0 !important; margin: 0 !important; }
        }
      `}</style>
      
      <div className="max-w-max-width-doc mx-auto mb-8 flex justify-between items-center px-4 md:px-0 print-hidden">
        <Link to="/" className="flex items-center gap-2 text-on-surface-variant hover:text-primary font-label-caps text-[11px] uppercase tracking-widest transition-colors">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          <span>Back to Forge</span>
        </Link>
        <button 
          onClick={handlePrint} 
          className="bg-surface border border-outline-variant px-6 py-2.5 rounded font-label-caps text-[11px] uppercase tracking-widest flex items-center gap-2 hover:bg-surface-container-high transition-all shadow-sm active:translate-y-px"
        >
          <span className="material-symbols-outlined text-[18px]">print</span>
          <span>Print to PDF</span>
        </button>
      </div>

      <div className="max-w-max-width-doc mx-auto bg-surface shadow-2xl p-8 sm:p-12 md:p-24 border border-outline-variant min-h-[1100px] print:shadow-none print:border-none print:max-w-none ink-drop-shadow transition-all relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-primary/20 print:hidden"></div>
        
        <div className="doc-content outline-none selection:bg-primary/20" dangerouslySetInnerHTML={{ __html: sanitized }} />
        
        <div className="mt-32 pt-12 border-t border-outline-variant flex flex-col md:flex-row items-center justify-between gap-6 opacity-40 grayscale hover:grayscale-0 transition-all print-hidden">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-on-surface flex items-center justify-center rounded">
              <span className="material-symbols-outlined text-primary text-[32px]">ink_pen</span>
            </div>
            <div>
              <span className="block font-label-caps text-[10px] font-bold tracking-[0.2em] text-on-surface uppercase">DocForge Editorial</span>
              <span className="block font-body-md text-[11px] text-on-surface-variant italic">Archived on {new Date(doc.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
               <span className="material-symbols-outlined text-[16px] text-primary">verified</span>
               <span className="font-label-caps text-[10px] font-bold text-on-surface uppercase tracking-widest">Archive Reference</span>
            </div>
            <span className="font-label-caps text-[12px] text-on-surface-variant uppercase tracking-widest opacity-60">REF-{doc.slug?.toUpperCase()}</span>
          </div>
        </div>
      </div>
      
      <div className="max-w-max-width-doc mx-auto py-12 text-center print-hidden">
        <p className="font-label-caps text-[10px] text-on-surface-variant tracking-widest uppercase">
          End of Manuscript · Generated by Scriptorium AI
        </p>
      </div>
    </div>
  );
}